<script>
import { MOLDURA_DO_THREAD } from '@modules/aawz-intelligence-v2/ui-registry/moldura-do-thread'
import { BUSCA_WEB } from '@modules/aawz-intelligence-v2/constants/Text.busca'
import {
  MOTIVO_DOMINIO_FORA_DA_ALLOWLIST,
  MOTIVO_IDENTIFICADOR_NO_TERMO,
} from '@modules/aawz-intelligence-v2/constants/Busca'
import {
  ESTADO_COM_FONTES,
  ESTADO_EM_ANDAMENTO,
  ESTADO_PAGINA_NAO_LIDA,
  ESTADO_RECUSADA,
  ESTADO_SEM_RESULTADO,
  ESTADO_TETO_ATINGIDO,
  buscasDoTurno,
} from '@modules/aawz-intelligence-v2/utils/buscas-do-turno'
import { aawzDate } from '@utils/dates/aawz-date'

// Edge case da US-6 ("dez resultados numa busca"): acima de três a lista
// colapsa — passando disso o bloco deixa de ser procedência e vira catálogo.
const MAXIMO_FONTES_VISIVEIS = 3

// `coletado_em` é `date-time` no contrato (`$defs.Citacao`). Só data: a hora
// da coleta não muda a decisão de ninguém e rouba a linha do domínio.
const MASCARA_DA_COLETA = 'DD/MM/YYYY'

/**
 * A frase da [T106]/[US-8] para cada estado de falha.
 *
 * Um mapa, e não uma cadeia de `if` no template: a condição que ESCOLHE a
 * frase precisa morar num lugar só, porque é ela que o teste de mutação troca.
 */
const FRASE_POR_ESTADO = {
  [ESTADO_PAGINA_NAO_LIDA]: (busca) => BUSCA_WEB.PAGINA_NAO_LIDA(busca.consulta),
  [ESTADO_SEM_RESULTADO]: (busca) => BUSCA_WEB.SEM_RESULTADO(busca.consulta),
  [ESTADO_TETO_ATINGIDO]: () => BUSCA_WEB.TETO_ATINGIDO,
  [ESTADO_RECUSADA]: (busca) => fraseDaRecusa(busca),
}

/**
 * A recusa nomeia o que o consultor pode FAZER, e cada motivo pede uma ação
 * diferente — dado de cliente se resolve reformulando, domínio bloqueado se
 * resolve com o gestor.
 *
 * @param {Object} busca A busca classificada.
 * @returns {String} A frase da recusa.
 */
function fraseDaRecusa(busca) {
  if (busca.motivo === MOTIVO_IDENTIFICADOR_NO_TERMO) {
    return BUSCA_WEB.RECUSADA_IDENTIFICADOR
  }
  if (busca.motivo === MOTIVO_DOMINIO_FORA_DA_ALLOWLIST) {
    return BUSCA_WEB.RECUSADA_DOMINIO(busca.ofensor || busca.consulta)
  }
  return BUSCA_WEB.RECUSADA(busca.motivo)
}

/**
 * Data da coleta já formatada — string vazia quando o campo não veio ou não é
 * data. `coletado_em` é OPCIONAL no contrato; "Invalid date" na tela seria
 * pior que nenhuma data.
 *
 * @param {String} coletadoEm O `coletado_em` da citação.
 * @returns {String} `DD/MM/YYYY`, ou vazio.
 */
function coletaFormatada(coletadoEm) {
  if (!coletadoEm) return ''
  const data = aawzDate(coletadoEm)
  if (!data.isValid()) return ''
  return data.format(MASCARA_DA_COLETA)
}

/**
 * Domínio da fonte — o do payload, ou o extraído da URL.
 *
 * O worker já manda `dominio` (`stream.py::_citacoes_de_conteudo` faz o
 * `urlparse`), mas o campo é opcional no contrato: sem este fallback, uma
 * fonte sem domínio perderia a única informação que o consultor lê de relance.
 *
 * @param {Object} fonte A citação do evento.
 * @returns {String} O domínio, ou vazio.
 */
function dominioDaFonte(fonte) {
  if (fonte.dominio) return String(fonte.dominio)
  const casamento = /^https?:\/\/([^/?#]+)/i.exec(String(fonte.url || ''))
  return casamento ? casamento[1] : ''
}

export default {
  name: 'IntelligenceV2BuscaWeb',
  props: {
    // Os payloads `d_busca` CRUS, na ordem de chegada — o que o getter
    // `buscaStreaming` entrega. A classificação é do `utils/buscas-do-turno`.
    eventos: {
      type: Array,
      default: function () {
        return []
      },
    },
  },
  data() {
    return {
      MOLDURA_DO_THREAD,
      BUSCA_WEB,
      // Nasce ABERTO: a procedência chega junto da resposta e é para ser lida
      // agora — colapsar é para depois (mesmo gesto de `servicos-citados`).
      aberto: true,
      // Índices das buscas cuja lista de fontes o consultor expandiu.
      expandidas: {},
    }
  },
  computed: {
    buscas() {
      return buscasDoTurno(this.eventos)
    },
    temBuscas() {
      return this.buscas.length > 0
    },
    resumo() {
      return String(this.buscas.length)
    },
    classeSeta() {
      return this.aberto ? 'ia-block-seta--aberta' : ''
    },
    // Tudo decorado aqui: o template não chama função (cada re-render a
    // reexecutaria) — convenção do módulo.
    buscasDecoradas() {
      const expandidas = this.expandidas
      return this.buscas.map(function (busca, indice) {
        return decorarBusca(busca, indice, expandidas[indice] === true)
      })
    },
  },
  methods: {
    alternar() {
      this.aberto = !this.aberto
    },
    alternarFontes(indice) {
      // `$set`: `expandidas` nasce `{}` e uma chave nova num objeto reativo do
      // Vue 2 não dispara render sem ele.
      this.$set(this.expandidas, indice, this.expandidas[indice] !== true)
    },
  },
}

/**
 * Uma busca classificada → a linha pronta para o template.
 *
 * @param {Object} busca Saída de `buscasDoTurno`.
 * @param {Number} indice Posição no turno (a chave da lista e do expandir).
 * @param {Boolean} expandida Se o consultor abriu a lista completa de fontes.
 * @returns {Object} A linha decorada.
 */
function decorarBusca(busca, indice, expandida) {
  const fontes = busca.fontes.map(decorarFonte)
  const ocultas = expandida ? 0 : Math.max(fontes.length - MAXIMO_FONTES_VISIVEIS, 0)
  const montarFrase = FRASE_POR_ESTADO[busca.estado]
  return {
    chave: indice,
    indice: indice,
    // A consulta é o que o consultor reconhece; sem par, o rótulo diz isso em
    // vez de deixar a linha muda.
    rotulo: busca.consulta || BUSCA_WEB.CONSULTA_DESCONHECIDA,
    emAndamento: busca.estado === ESTADO_EM_ANDAMENTO,
    temFontes: busca.estado === ESTADO_COM_FONTES,
    // A frase de falha, ou vazio no caminho feliz. ★ É esta linha que a [T106]
    // entrega: sem `montarFrase`, a falha ficaria invisível na tela.
    frase: montarFrase ? montarFrase(busca) : '',
    fontes: expandida ? fontes : fontes.slice(0, MAXIMO_FONTES_VISIVEIS),
    ocultas: ocultas,
    rotuloVerMais: ocultas ? BUSCA_WEB.VER_TODAS(ocultas) : BUSCA_WEB.VER_MENOS,
    // Só há o que alternar com mais fontes do que cabem.
    podeAlternarFontes: fontes.length > MAXIMO_FONTES_VISIVEIS,
    permitido: busca.permitido || '',
  }
}

function decorarFonte(fonte, indice) {
  const coletada = coletaFormatada(fonte.coletado_em)
  const dominio = dominioDaFonte(fonte)
  return {
    chave: (fonte.url || 'fonte') + '-' + indice,
    url: fonte.url || '',
    // O título do provedor pode vir vazio; o domínio é o que sobra de legível.
    titulo: fonte.titulo || dominio || fonte.url || '',
    dominio: dominio,
    // Uma linha só: `bcb.gov.br · coletado em 10/09/2026`.
    legenda: coletada ? dominio + ' · ' + BUSCA_WEB.COLETADO_EM + ' ' + coletada : dominio,
    trecho: fonte.trecho || '',
  }
}
</script>

<template>
  <div v-if="temBuscas" class="busca-web ia-block-card" v-bind="MOLDURA_DO_THREAD">
    <!-- `button`, e não `div`: sem foco e sem Enter/Espaço o bloco colapsado
         fica inalcançável por teclado. Mesmo elemento da `raciocinio-linha`. -->
    <button
      type="button"
      class="ia-block-card__cabecalho ia-block-card__cabecalho--fita ia-block-card__cabecalho--clicavel"
      :aria-expanded="String(aberto)"
      :aria-label="BUSCA_WEB.ARIA_LISTA"
      data-test="busca-web-cabecalho"
      @click="alternar"
    >
      <icon-svg
        name="search-normal"
        :size="14"
        color="var(--primary)"
        class="ia-block-card__icone"
      />
      <span class="ia-block-card__titulo">{{ BUSCA_WEB.TITULO }}</span>
      <span class="ia-block-card__meta">{{ resumo }}</span>
      <icon-svg
        name="arrow-down-new-ux"
        :size="14"
        color="var(--neutral-700)"
        class="ia-block-seta"
        :class="classeSeta"
      />
    </button>

    <div
      v-for="busca in buscasDecoradas"
      v-show="aberto"
      :key="busca.chave"
      class="busca-web__item"
      data-test="busca-web-item"
    >
      <div class="busca-web__consulta ia-texto-auxiliar">
        <span v-if="busca.emAndamento" class="busca-web__prefixo">
          {{ BUSCA_WEB.EM_ANDAMENTO }}:
        </span>
        {{ busca.rotulo }}
      </div>

      <!-- ★ [T106] A frase da falha. `role="alert"` e não `aria-live`: é uma
           afirmação sobre o que NÃO entrou na resposta, e o leitor de tela tem
           de recebê-la mesmo que o foco esteja em outro lugar. -->
      <p
        v-if="busca.frase"
        class="busca-web__frase ia-texto-auxiliar"
        role="alert"
        data-test="busca-web-frase"
      >
        {{ busca.frase }}
      </p>

      <p v-if="busca.permitido" class="busca-web__permitido ia-texto-auxiliar">
        {{ BUSCA_WEB.DOMINIOS_LIBERADOS }} {{ busca.permitido }}
      </p>

      <!-- O cartão de fonte da US-6: título, domínio, URL clicável e data de
           coleta — montado do PAYLOAD, nunca de link escrito pelo modelo (o
           front apaga toda âncora da saída dele, `neutralizar-ancoras.js`). -->
      <ul v-if="busca.temFontes" class="busca-web__fontes">
        <li
          v-for="fonte in busca.fontes"
          :key="fonte.chave"
          class="busca-web__fonte"
          data-test="busca-web-fonte"
        >
          <!-- `rel="noopener noreferrer"`: sem ele a página aberta ganha
               `window.opener` (reverse tabnabbing) e recebe o Referer com o
               `id_escritorio` na URL do chat. -->
          <a
            class="busca-web__fonte-link"
            :href="fonte.url"
            target="_blank"
            rel="noopener noreferrer"
            :aria-label="BUSCA_WEB.ABRIR_FONTE"
            data-test="busca-web-fonte-link"
          >
            <span class="busca-web__fonte-titulo ia-texto-auxiliar">{{ fonte.titulo }}</span>
            <icon-svg name="go-out" :size="12" color="var(--neutral-700)" />
          </a>
          <span class="busca-web__fonte-legenda ia-texto-auxiliar">{{ fonte.legenda }}</span>
          <span v-if="fonte.trecho" class="busca-web__fonte-trecho ia-texto-auxiliar">
            {{ fonte.trecho }}
          </span>
        </li>
      </ul>

      <button
        v-if="busca.podeAlternarFontes"
        type="button"
        class="busca-web__ver-mais ia-texto-selo"
        data-test="busca-web-ver-mais"
        @click="alternarFontes(busca.indice)"
      >
        {{ busca.rotuloVerMais }}
      </button>
    </div>
  </div>
</template>

<style lang="sass" scoped>
// Respiro contra o bloco anterior. Mora aqui, e não numa utilitária no
// consumidor: a distância é do bloco, não de quem o coloca.
.busca-web
  margin-top: 12px

// Reset do `button` do cabeçalho — sem isto ele traz borda, fundo e fonte
// nativos e a fita destoa dos outros blocos da resposta.
.ia-block-card__cabecalho
  width: 100%
  text-align: left
  font: inherit
  border: 0

// O reset acima ganha da partial por especificidade (o `scoped` soma um
// seletor de atributo) e levava junto a linha da fita. Redeclarada com o MESMO
// token de `_ia-block-card.sass`.
.ia-block-card__cabecalho--fita
  border-bottom: 1px solid var(--border-hairline)

.busca-web__item
  display: flex
  flex-direction: column
  gap: 6px
  padding: 10px 14px

.busca-web__item + .busca-web__item
  border-top: 1px solid var(--border-hairline)

.busca-web__consulta
  color: var(--neutral-900)
  // Consulta longa (URL inteira) quebra em vez de esticar o card.
  overflow-wrap: anywhere

.busca-web__prefixo
  font-weight: 400
  color: var(--neutral-700)

// A falha é informação, não alarme: `--danger-text` pintaria de vermelho um
// turno que seguiu e respondeu. O tom secundário é o mesmo do motivo dos
// serviços citados.
.busca-web__frase
  color: var(--neutral-700)
  margin: 0

.busca-web__permitido
  color: var(--neutral-700)
  margin: 0

.busca-web__fontes
  // Reset do `<ul>`: sem isto vêm o recuo e o marcador do user-agent.
  list-style: none
  margin: 0
  padding: 0
  display: flex
  flex-direction: column
  gap: 8px

.busca-web__fonte
  display: flex
  flex-direction: column
  gap: 2px
  min-width: 0

.busca-web__fonte-link
  display: inline-flex
  align-items: center
  gap: 4px
  min-width: 0
  color: var(--neutral-900)
  text-decoration: none

  &:hover .busca-web__fonte-titulo
    text-decoration: underline

.busca-web__fonte-titulo
  overflow: hidden
  text-overflow: ellipsis
  white-space: nowrap

.busca-web__ver-mais
  color: var(--primary)
  align-self: flex-start
  padding: 0
  border: 0
  background: none
  cursor: pointer

  &:hover
    text-decoration: underline

// A cor do papel (leva 2 da escala: a classe `ia-texto-*` não carrega cor).
.busca-web__fonte-legenda
  color: var(--neutral-700)

// A cor do papel (leva 2 da escala: a classe `ia-texto-*` não carrega cor).
.busca-web__fonte-trecho
  color: var(--neutral-700)

</style>
